import { jsx as _jsx } from "react/jsx-runtime";
import { css } from '@emotion/react';
import styled from '@emotion/styled';
import { useMemo } from 'react';
import { Palette } from '../../constants/palette';
export const checkboxSizes = {
    s: 16,
    m: 24,
    l: 40,
};
/**
 * Possible colors of the checkmark inside the checkbox.
 */
const checkmarkIconColor = {
    white: Palette.white,
    blue: Palette.blue,
    gray: Palette.gray90,
};
/**
 * Returns the URL for the background image of the checkbox for a given size
 * and color.
 *
 * @param size - Size of the checkmark.
 * @param color - Color of the checkmark.
 */
const getCheckmarkIconVariants = (size, color) => {
    switch (size) {
        case 'm':
            return `"data:image/svg+xml,%3Csvg width='11' height='8' viewBox='0 0 11 8' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M10.0004 1L4.00013 7L1 4.00013' stroke='${encodeURIComponent(checkmarkIconColor[color])}' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'/%3E%3C/svg%3E%0A"`;
        case 'l':
            return `"data:image/svg+xml,%3Csvg width='18' height='12' viewBox='0 0 18 12' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M16.6672 1L6.66673 11L1.6665 6.00022' stroke='${encodeURIComponent(checkmarkIconColor[color])}' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'/%3E%3C/svg%3E"`;
        case 's':
        default:
            return `"data:image/svg+xml,%3Csvg width='9' height='6' viewBox='0 0 9 6' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M7.66677 1L3.66659 5L1.6665 3.00009' stroke='${encodeURIComponent(checkmarkIconColor[color])}' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'/%3E%3C/svg%3E%0A"`;
    }
};
/**
 * Background images for the background of the checkmark for every size and
 * color variant.
 */
const checkmarkIcon = {
    green: {
        s: getCheckmarkIconVariants('s', 'white'),
        m: getCheckmarkIconVariants('m', 'white'),
        l: getCheckmarkIconVariants('l', 'white'),
    },
    blue: {
        s: getCheckmarkIconVariants('s', 'white'),
        m: getCheckmarkIconVariants('m', 'white'),
        l: getCheckmarkIconVariants('l', 'white'),
    },
    white: {
        s: getCheckmarkIconVariants('s', 'blue'),
        m: getCheckmarkIconVariants('m', 'blue'),
        l: getCheckmarkIconVariants('l', 'blue'),
    },
};
/**
 * Background images for the background of the disabled checkmark for every size
 * and color variant.
 */
const checkmarkIconDisabled = {
    green: {
        s: getCheckmarkIconVariants('s', 'white'),
        m: getCheckmarkIconVariants('m', 'white'),
        l: getCheckmarkIconVariants('l', 'white'),
    },
    blue: {
        s: getCheckmarkIconVariants('s', 'white'),
        m: getCheckmarkIconVariants('m', 'white'),
        l: getCheckmarkIconVariants('l', 'white'),
    },
    white: {
        s: getCheckmarkIconVariants('s', 'gray'),
        m: getCheckmarkIconVariants('m', 'gray'),
        l: getCheckmarkIconVariants('l', 'gray'),
    },
};
/**
 * Width of the checkmark as a percentage of the checkbox width.
 */
const checkmarkSizes = {
    s: 50,
    m: 55,
    l: 55,
};
const checkboxColors = {
    green: Palette.goGreen,
    blue: Palette.blue,
    white: Palette.white,
};
const checkboxColorsDisabled = {
    green: Palette.gray90,
    blue: Palette.gray90,
    white: Palette.white,
};
const checkboxBorderRadius = {
    square: 2,
    circle: Number.MAX_SAFE_INTEGER,
};
const getCheckboxDimensionStyles = (size) => css `
  height: ${checkboxSizes[size]}px;
  width: ${checkboxSizes[size]}px;
`;
const CheckboxTag = styled.input `
  ${({ elementSize }) => getCheckboxDimensionStyles(elementSize)};
  background-color: #fff;
  flex-shrink: 0;
  appearance: none;

  border: ${({ theme, error }) => error
    ? `1px solid ${theme.colors.error}`
    : `1px solid ${theme.colors.border}`};
  border-radius: ${({ variant }) => checkboxBorderRadius[variant !== null && variant !== void 0 ? variant : 'square']}px;

  background-image: url(${({ elementSize, color }) => checkmarkIcon[color][elementSize]});
  background-position: center;
  background-repeat: no-repeat;
  background-size: 0;
  margin: 0;
  cursor: pointer;

  transition-property: background-size, border, background-color;
  transition-duration: 0.2s;
  transition-timing-function: ease;

  &:disabled {
    border-color: ${({ theme }) => theme.colors.disabled}};
    background-image: url(${({ elementSize, color }) => checkmarkIconDisabled[color][elementSize]});
  }

  &:checked {
    background-color: ${({ color }) => checkboxColors[color]};
    background-size: ${({ elementSize }) => checkmarkSizes[elementSize]}%;

    border-color: ${({ color }) => checkboxColors[color]};

    &:disabled {
      background-color: ${({ theme }) => theme.colors.disabled};
      background-color: ${({ color }) => checkboxColorsDisabled[color]};
      border-color: ${({ theme }) => theme.colors.disabled};
    }
  }
`;
/**
 * Checkbox
 *
 * @description Checkbox component.
 *
 * @component
 * @example
 * return (
 *   <Checkbox
 *     size="m"
 *     color="green"
 *     isChecked={true}
 *     onClick={() => { ... }}
 *   />
 * )
 */
export const Checkbox = ({ size = 's', variant = 'square', color = 'green', dataCy, ...rest }) => {
    const cyName = useMemo(() => {
        if (dataCy) {
            return dataCy;
        }
        if (rest.id) {
            return `checkbox-${rest.id}`;
        }
        if (rest.name) {
            return `checkbox-${rest.name}`;
        }
        return 'checkbox-unknown';
    }, [rest.id, rest.name, dataCy]);
    return (_jsx(CheckboxTag, { type: "checkbox", color: color, elementSize: size, variant: variant, "data-cy": cyName, ...(rest.onChange
            ? rest
            : { ...rest, checked: undefined, defaultChecked: rest.checked }) }));
};
//# sourceMappingURL=data:application/json;base64,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